import { useQueryClient } from '@tanstack/react-query'
import { compact, uniqBy } from 'lodash'
import { ComponentProps, useState } from 'react'

import { Booking } from '../../models/Booking'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ProListTableCell } from './ProListTableCell'
import { ServiceListTableCell } from './ServiceListTableCell'

type Props = {
    dashboard?: Dashboard
    bookings: Booking[]
    searchText: string
    isLoading: boolean
    viewFilter: number
}

export const render = ({
    base: {
        props: { dashboard, bookings, searchText, isLoading, viewFilter },
        showProgressBar,
        hideProgressBar,
        logAndDisplayError,
    },
}: PocketComponentProps<unknown, Props>) => {
    const queryClient = useQueryClient()

    const [confirmationModalState, setConfirmationModalState] = useState<{
        type: 'check-in' | 'check-out' | 'auto-assign'
        booking: Booking
    } | null>(null)

    const handleCheckInBooking = async (booking: Booking) => {
        showProgressBar()
        await RecordFactory.getInstance().checkinBooking(booking)
        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        hideProgressBar()
    }

    const handleCheckOutBooking = async (booking: Booking) => {
        showProgressBar()
        await RecordFactory.getInstance().checkoutBooking(booking)
        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        hideProgressBar()
    }

    const handleAutoAssignBooking = async (booking: Booking) => {
        showProgressBar()
        const result = await RecordFactory.getInstance().assignBooking(booking)
        if (!result.ok) {
            logAndDisplayError(result.error)
            hideProgressBar()
            return
        }

        await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        hideProgressBar()
    }

    const selectedViewOptionLabel =
        dashboard?.viewOptions.find(({ value }) => value === viewFilter.toString())?.label ??
        dashboard?.viewOptions?.at(0)?.label ??
        'Active'

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${selectedViewOptionLabel} ${bookings.length === 1 ? 'reservation' : 'reservations'}`,
                        value: bookings.length.toString(),
                    },
                ]}
            />

            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Reservation</TableHead>
                        <TableHead className="font-normal">Check-in</TableHead>
                        <TableHead className="font-normal">Check-out</TableHead>
                        <TableHead className="font-normal">Pro</TableHead>
                        <TableHead></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {bookings.map((booking) => {
                        const moreMenuOptions: ComponentProps<typeof MoreMenuButton>['options'] =
                            compact([
                                booking.allowCheckin && {
                                    id: 'check-in',
                                    title: 'Check-in client',
                                    icon: 'calendarArrowLeft',
                                },
                                booking.allowCheckout && {
                                    id: 'check-out',
                                    title: 'Check-out client',
                                    icon: 'calendarArrowRight',
                                },
                                booking.allowAutoAssign && {
                                    id: 'auto-assign',
                                    title: 'Auto-assign',
                                    icon: 'user',
                                },
                            ])

                        const pros = uniqBy(
                            booking.jobs.map((j) => j.person).filter((p) => !!p) as User[],
                            (user) => user.id
                        )

                        return (
                            <ClickableTableRow
                                key={booking.id}
                                className="cursor-pointer"
                                href={`/bookings/${booking.id}`}
                            >
                                <TableCell>
                                    <ClientTableCell
                                        user={booking.client}
                                        subaccount={booking.subaccount}
                                    />
                                </TableCell>
                                <TableCell>
                                    <ServiceListTableCell
                                        services={booking.lines}
                                        icon="calendarClock.small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {booking.checkinStartDate !== null && (
                                        <Paragraph
                                            className="whitespace-nowrap text-gray-700"
                                            size="sm"
                                        >
                                            {booking.checkinStartDate.format('ddd, MMM D')}
                                        </Paragraph>
                                    )}
                                    <Paragraph
                                        className="whitespace-nowrap text-gray-700"
                                        size="sm"
                                    >
                                        {booking.checkinTimeDescription}
                                    </Paragraph>
                                </TableCell>
                                <TableCell>
                                    {booking.checkoutStartDate !== null && (
                                        <Paragraph
                                            className="whitespace-nowrap text-gray-700"
                                            size="sm"
                                        >
                                            {booking.checkoutStartDate.format('ddd, MMM D')}
                                        </Paragraph>
                                    )}
                                    <Paragraph
                                        className="whitespace-nowrap text-gray-700"
                                        size="sm"
                                    >
                                        {booking.checkoutTimeDescription}
                                    </Paragraph>
                                </TableCell>
                                <TableCell>
                                    <ProListTableCell
                                        pros={pros}
                                        size="xs"
                                        showLabel={pros.length === 1}
                                    />
                                </TableCell>
                                <TableCell>
                                    {viewFilter !== 4 && (
                                        <div className="flex items-center justify-end gap-x-6">
                                            {booking.allowCheckin ? (
                                                <Button
                                                    intent="secondary"
                                                    size="small"
                                                    leftIcon="calendarArrowLeft.small"
                                                    className="whitespace-nowrap"
                                                    onClick={(
                                                        event: React.MouseEvent<HTMLButtonElement>
                                                    ) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                        setConfirmationModalState({
                                                            type: 'check-in',
                                                            booking,
                                                        })
                                                    }}
                                                >
                                                    Check-in client
                                                </Button>
                                            ) : booking.allowCheckout ? (
                                                <Button
                                                    intent="secondary"
                                                    size="small"
                                                    leftIcon="calendarArrowRight.small"
                                                    className="whitespace-nowrap"
                                                    onClick={(
                                                        event: React.MouseEvent<HTMLButtonElement>
                                                    ) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                        setConfirmationModalState({
                                                            type: 'check-out',
                                                            booking,
                                                        })
                                                    }}
                                                >
                                                    Check-out client
                                                </Button>
                                            ) : booking.allowAutoAssign ? (
                                                <Button
                                                    intent="secondary"
                                                    size="small"
                                                    leftIcon="user.small"
                                                    className="whitespace-nowrap"
                                                    onClick={(
                                                        event: React.MouseEvent<HTMLButtonElement>
                                                    ) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                        setConfirmationModalState({
                                                            type: 'auto-assign',
                                                            booking,
                                                        })
                                                    }}
                                                >
                                                    Auto-assign
                                                </Button>
                                            ) : null}

                                            {moreMenuOptions.length > 0 && (
                                                <MoreMenuButton
                                                    options={moreMenuOptions}
                                                    onSelectOption={(option) => {
                                                        switch (option.id) {
                                                            case 'check-in':
                                                                handleCheckInBooking(booking)
                                                                break
                                                            case 'check-out':
                                                                handleCheckOutBooking(booking)
                                                                break
                                                            case 'auto-assign':
                                                                handleAutoAssignBooking(booking)
                                                                break
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {bookings.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>

            {confirmationModalState !== null && (
                <ConfirmationModal
                    isOpen
                    onClose={() => setConfirmationModalState(null)}
                    onConfirm={() => {
                        switch (confirmationModalState.type) {
                            case 'check-in':
                                handleCheckInBooking(confirmationModalState.booking)
                                break
                            case 'check-out':
                                handleCheckOutBooking(confirmationModalState.booking)
                                break
                            case 'auto-assign':
                                handleAutoAssignBooking(confirmationModalState.booking)
                                break
                        }
                        setConfirmationModalState(null)
                    }}
                    title={`${
                        confirmationModalState.type === 'check-in'
                            ? 'Check-in '
                            : confirmationModalState.type === 'check-out'
                              ? 'Check-out '
                              : 'Auto-assign '
                    } ${confirmationModalState.booking.client?.name ?? 'client'}?`}
                    confirmButtonText={`${
                        confirmationModalState.type === 'check-in'
                            ? 'Yes, check-in'
                            : confirmationModalState.type === 'check-out'
                              ? 'Yes, check-out'
                              : 'Yes, auto-assign'
                    }`}
                >
                    <Paragraph>This action cannot be undone.</Paragraph>
                </ConfirmationModal>
            )}
        </div>
    )
}
export const ReservationsDashboard = usePSComponent(render)
